import { useState, useMemo, Fragment, ReactNode } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  OnChangeFn,
  getExpandedRowModel,
  ExpandedState,
} from '@tanstack/react-table';
import { AnimatePresence } from 'framer-motion';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  AnimatedTableRow,
} from '@predoc/cow';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  emptyState?: ReactNode;
  accordionContent?: (row: TData) => React.ReactNode;
  grouping?: string[];
}

type RowSpanInfo = { isFirst: boolean; span: number };

const DataTable = <TData, TValue>({
  columns,
  data,
  sorting = [],
  onSortingChange,
  emptyState,
  accordionContent,
  grouping,
}: DataTableProps<TData, TValue>) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const groupColumnId = grouping?.[0];
  const isGrouped = !!groupColumnId;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      expanded,
    },
    onSortingChange: onSortingChange,
    onExpandedChange: setExpanded,
    getRowCanExpand: () => !!accordionContent,
    enableMultiSort: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
  });

  const rows = table.getRowModel().rows;

  const rowSpanByRowId = useMemo(() => {
    if (!groupColumnId) return null;
    const info = new Map<string, RowSpanInfo>();
    let i = 0;
    while (i < rows.length) {
      const currentValue = String(rows[i].getValue(groupColumnId) ?? '');
      let j = i + 1;
      while (
        j < rows.length &&
        String(rows[j].getValue(groupColumnId) ?? '') === currentValue
      ) {
        j += 1;
      }
      const span = j - i;
      info.set(rows[i].id, { isFirst: true, span });
      for (let k = i + 1; k < j; k += 1) {
        info.set(rows[k].id, { isFirst: false, span: 0 });
      }
      i = j;
    }
    return info;
  }, [rows, groupColumnId]);

  const hasRows = rows.length > 0;
  return (
    <div className="overflow-x-auto rounded-md border shadow-sm">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta as
                  | { className?: string }
                  | undefined;
                return (
                  <TableHead key={header.id} className={meta?.className}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        {hasRows ? (
          <TableBody>
            {rows.map((row) => {
              const rowSpanInfo = rowSpanByRowId?.get(row.id);
              return (
                <Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() && 'selected'}
                    className={`group ${!row.getIsExpanded() || isGrouped ? 'bg-white' : ''}`}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta as
                        | { className?: string }
                        | undefined;

                      if (
                        isGrouped &&
                        cell.column.id === groupColumnId &&
                        rowSpanInfo
                      ) {
                        if (!rowSpanInfo.isFirst) return null;
                        return (
                          <TableCell
                            key={cell.id}
                            className={`align-top ${rowSpanInfo.span > 1 ? 'border-r' : ''} ${meta?.className ?? ''}`}
                            rowSpan={rowSpanInfo.span}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={cell.id} className={meta?.className}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {!isGrouped && (
                    <AnimatePresence>
                      {row.getIsExpanded() && (
                        <AnimatedTableRow key={`${row.id}-expanded`}>
                          <TableCell colSpan={columns.length} className="p-0">
                            {accordionContent?.(row.original)}
                          </TableCell>
                        </AnimatedTableRow>
                      )}
                    </AnimatePresence>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        ) : emptyState ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} className="p-0">
                {emptyState}
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
    </div>
  );
};

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}
const TableSkeleton = ({
  rows = 5,
  columns = 6,
  showHeader = true,
  className = '',
}: TableSkeletonProps) => {
  return (
    <div className={`rounded-lg border bg-white shadow-sm ${className}`}>
      <Table>
        {showHeader && (
          <TableHeader>
            <TableRow>
              {Array.from({ length: columns }).map((_, index) => (
                <TableHead
                  key={index}
                  className="text-muted-foreground text-xs font-medium tracking-wider"
                >
                  <div className="animate-pulse">
                    <div className="bg-muted h-4 w-20 rounded"></div>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <TableCell key={colIndex}>
                  <div className="animate-pulse">
                    <div className="bg-muted h-4 w-full rounded"></div>
                  </div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export { DataTable, TableSkeleton };
